/**
 * DCMA 14-point checks plus the schedule-health counts used by the narrative.
 * Float and duration thresholds use the activity calendar's hours per day
 * (dataset HoursPerDay, else 8).
 *
 * Relationship count, shared with the narrative health line: one link per
 * distinct predecessor (predecessor task id + FS/SS/FF/SF) on a discrete
 * successor. Discrete means not TT_LOE and not TT_WBS. Completed activities
 * stay in the count. A missing predecessor calendar does not drop the link;
 * the predecessor activity row has to exist. Duplicate TASKPRED rows count once.
 *
 * Future actuals, shared with the narrative: one discrete activity when its
 * actual start or actual finish is after the P6 data date. That date is
 * PROJECT.last_recalc_date (datasetDataDateMs), not the WBS "as of" label.
 * An activity with both dates after the data date counts once.
 */
import { calendarForActivity } from '../workCalendar.js';

function ymd(value) {
	if (value == null || value === '' || value === 0) return '';
	if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
		return value.slice(0, 10);
	}
	const n = Number(value);
	if (!n || n < 0) return '';
	try {
		return new Date(n).toISOString().slice(0, 10);
	} catch (e) {
		return '';
	}
}

function isComplete(status) {
	return status === 'TK_Complete' || status === 'TK_Done';
}

function relType(rel) {
	const t = String(rel.Type || rel.pred_type || '').toUpperCase();
	if (t.endsWith('FS') || t === 'PR_FS') return 'FS';
	if (t.endsWith('SS') || t === 'PR_SS') return 'SS';
	if (t.endsWith('FF') || t === 'PR_FF') return 'FF';
	if (t.endsWith('SF') || t === 'PR_SF') return 'SF';
	return t;
}

function lagDays(rel) {
	const n = Number(rel.Lag != null ? rel.Lag : rel.lag_hr_cnt);
	return Number.isNaN(n) ? 0 : n;
}

function calcPct(num, den) {
	return den > 0 ? (num / den) * 100 : 0;
}

function metric(id, name, description, value, total, status, threshold) {
	return {
		id,
		name,
		description,
		value,
		total,
		percentage: calcPct(value, total),
		status,
		threshold
	};
}

function hoursFor(activity, options) {
	const fallback = Number(options && options.hoursPerDay);
	const fallbackHours = (fallback >= 1 && fallback <= 24) ? fallback : 8;
	if (options && options.dataset) {
		const cal = calendarForActivity(activity, options.dataset);
		const h = Number(cal && (cal.dayHrCnt != null ? cal.dayHrCnt : cal.day_hr_cnt));
		if (cal && cal.id && h >= 1 && h <= 24) return h;
	}
	return fallbackHours;
}

function dataDateYmd(options) {
	if (options && options.dataDate) {
		const day = ymd(options.dataDate);
		if (day) return { day, fromSchedule: true };
	}
	return { day: new Date().toISOString().slice(0, 10), fromSchedule: false };
}

function predIsFinished(rel) {
	if (!rel) return false;
	if (rel.IsActualFinish === 1 || rel.IsActualFinish === true || rel.IsActualFinish === '1') return true;
	const status = rel.statusCode || rel.status_code || '';
	return status === 'TK_Complete' || status === 'TK_Done';
}

function hasProgress(activity) {
	if (isComplete(activity.statusCode)) return true;
	if (Number(activity.percentComplete) > 0) return true;
	return Boolean(ymd(activity.actualStart));
}

function discreteRelationships(activity) {
	const seen = new Set();
	const out = [];
	(activity.predecessors || []).forEach((rel, index) => {
		const pred = rel.TaskId != null ? rel.TaskId : (rel.pred_task_id != null ? rel.pred_task_id : (rel.ID != null ? rel.ID : ('i' + index)));
		const key = String(pred) + '|' + relType(rel);
		if (seen.has(key)) return;
		seen.add(key);
		out.push(rel);
	});
	return out;
}

export function calculateDcmaMetrics(activities, options) {
	const list = (activities || []).filter(Boolean);
	if (!list.length) return [];
	const opts = options || {};

	const discrete = list.filter(t => t.taskType !== 'TT_LOE' && t.taskType !== 'TT_WBS');
	const totalDiscrete = discrete.length;
	const work = discrete.filter(t => t.taskType === 'TT_Task' || t.taskType === 'TT_Rsrc');

	let missingLogicCount = 0;
	discrete.forEach(t => {
		const isStartMile = t.taskType === 'TT_Mile';
		const isFinishMile = t.taskType === 'TT_FinMile';
		const hasPreds = Array.isArray(t.predecessors) && t.predecessors.length > 0;
		const hasSuccs = Array.isArray(t.successors) && t.successors.length > 0;
		if ((!isStartMile && !hasPreds) || (!isFinishMile && !hasSuccs)) {
			missingLogicCount++;
		}
	});

	const rels = [];
	discrete.forEach(t => {
		discreteRelationships(t).forEach(p => rels.push(p));
	});
	const totalRels = rels.length;
	const leadsCount = rels.filter(r => lagDays(r) < 0).length;
	const lagsCount = rels.filter(r => lagDays(r) > 0).length;
	const typeCounts = { FS: 0, SS: 0, FF: 0, SF: 0, other: 0 };
	rels.forEach(r => {
		const kind = relType(r);
		if (typeCounts[kind] == null) typeCounts.other++;
		else typeCounts[kind]++;
	});
	const nonFsCount = totalRels - typeCounts.FS;

	const hardConstraintTypes = ['CS_MANDSTART', 'CS_MANDFIN', 'CS_MSO', 'CS_MFO'];
	const hardConstraintsCount = discrete.filter(t => {
		const c = t.constraintType || t.cstr_type;
		return c && hardConstraintTypes.includes(c);
	}).length;

	const incompleteTasks = discrete.filter(t => !isComplete(t.statusCode));
	const highFloatCount = incompleteTasks.filter(t => Number(t.totalFloat) > (44 * hoursFor(t, opts))).length;
	const negativeFloatCount = incompleteTasks.filter(t => Number(t.totalFloat) < 0).length;
	const highDurationCount = discrete.filter(t => Number(t.targetDuration) > (44 * hoursFor(t, opts))).length;
	const longDurationCount = work.filter(t => Number(t.targetDuration) > (20 * hoursFor(t, opts))).length;

	const dateInfo = dataDateYmd(opts);
	const dataDateStr = dateInfo.day;
	let invalidDatesCount = 0;
	let forecastPast = 0;
	let futureActuals = 0;
	discrete.forEach(t => {
		if (!isComplete(t.statusCode)) {
			const earlyStart = ymd(t.earlyStart || t.start);
			if (earlyStart && earlyStart < dataDateStr && !ymd(t.actualStart)) {
				invalidDatesCount++;
				forecastPast++;
			}
		}
		const actStart = ymd(t.actualStart);
		const actFinish = ymd(t.actualFinish);
		const futureStart = actStart && actStart > dataDateStr;
		const futureFinish = actFinish && actFinish > dataDateStr;
		if (futureStart || futureFinish) {
			invalidDatesCount++;
			futureActuals++;
		}
	});

	let missingResourceCount = 0;
	work.forEach(t => {
		const named = t.resources && t.resources.length;
		const details = t.resourceDetails && t.resourceDetails.length;
		if (!named && !details) missingResourceCount++;
	});
	const costKnown = work.some(t => t.targetCost != null && t.targetCost !== '');
	const uncostedCount = costKnown ? work.filter(t => Number(t.targetCost) <= 0).length : 0;

	const completedTasks = discrete.filter(t => isComplete(t.statusCode));
	let missedTasksCount = 0;
	completedTasks.forEach(t => {
		const actFinish = ymd(t.actualFinish);
		const targetFinish = ymd(t.targetFinish);
		if (actFinish && targetFinish && actFinish > targetFinish) missedTasksCount++;
	});

	let oosCount = 0;
	discrete.forEach(t => {
		if (!hasProgress(t)) return;
		const oos = (t.predecessors || []).some(p => relType(p) === 'FS' && !predIsFinished(p));
		if (oos) oosCount++;
	});

	const loeTasks = list.filter(t => t.taskType === 'TT_LOE');
	const loeDriving = loeTasks.filter(t => t.drivingPathFlag === 'Y' || t.driving === true).length;
	const stalled = list.filter(t => t.statusCode === 'TK_Active' && Number(t.percentComplete) === 0);

	const missingLogicPct = calcPct(missingLogicCount, totalDiscrete);
	const lagsPct = calcPct(lagsCount, totalRels);
	const relsPct = calcPct(nonFsCount, totalRels);
	const constraintsPct = calcPct(hardConstraintsCount, totalDiscrete);
	const highFloatPct = calcPct(highFloatCount, incompleteTasks.length);
	const durationPct = calcPct(highDurationCount, totalDiscrete);
	const longPct = calcPct(longDurationCount, work.length);
	const missedPct = calcPct(missedTasksCount, completedTasks.length);
	const dateLabel = dateInfo.fromSchedule ? 'the data date' : 'today';

	const metrics = [
		metric('logic', 'Missing Logic', 'Activities missing a predecessor (start milestones excluded) or a successor (finish milestones excluded).', missingLogicCount, totalDiscrete, missingLogicPct <= 5 ? 'PASS' : 'FAIL', '< 5%'),
		metric('leads', 'Leads (Neg Lag)', 'Relationships with negative lag (leads) which can distort flow.', leadsCount, totalRels, leadsCount === 0 ? 'PASS' : 'FAIL', '0 allowed'),
		metric('lags', 'Lags (Pos Lag)', 'Relationships with positive lag. Should be minimized.', lagsCount, totalRels, lagsPct <= 5 ? 'PASS' : 'FAIL', '< 5%'),
		metric('rels', 'Relationship Types', `Non Finish-to-Start relationships (SS, FF, SF). Mix: FS ${typeCounts.FS}, SS ${typeCounts.SS}, FF ${typeCounts.FF}, SF ${typeCounts.SF}.`, nonFsCount, totalRels, relsPct <= 10 ? 'PASS' : 'WARNING', '< 10%'),
		metric('constraints', 'Hard Constraints', 'Tasks with hard constraints (Mandatory Start/Finish, Start/Finish On). Blank constraint types are ignored.', hardConstraintsCount, totalDiscrete, constraintsPct <= 5 ? 'PASS' : 'WARNING', '< 5%'),
		metric('float_high', 'High Float', 'Incomplete tasks with total float > 44 working days on the activity calendar.', highFloatCount, incompleteTasks.length, highFloatPct <= 5 ? 'PASS' : 'WARNING', '< 5%'),
		metric('float_neg', 'Negative Float', 'Tasks with total float < 0. Indicates schedule delay.', negativeFloatCount, incompleteTasks.length, negativeFloatCount === 0 ? 'PASS' : 'FAIL', '0 allowed'),
		metric('duration', 'High Duration', 'Tasks with duration > 44 working days on the activity calendar.', highDurationCount, totalDiscrete, durationPct <= 5 ? 'PASS' : 'WARNING', '< 5%'),
		metric('duration_long', 'Long Duration', 'Work activities with duration > 20 working days on the activity calendar.', longDurationCount, work.length, longPct <= 5 ? 'PASS' : 'WARNING', '< 5%'),
		metric('dates', 'Invalid Dates', `Forecast dates before ${dateLabel} (${forecastPast}) or actual dates after it (${futureActuals}).`, invalidDatesCount, totalDiscrete, invalidDatesCount === 0 ? 'PASS' : 'FAIL', '0 allowed'),
		metric('resources', 'No Resources', 'Work activities missing resource assignments.', missingResourceCount, work.length, missingResourceCount === 0 ? 'PASS' : 'WARNING', '0 allowed (Best Practice)'),
		metric('missed', 'Missed Tasks', 'Completed tasks that finished after their baseline/target date.', missedTasksCount, completedTasks.length, missedPct <= 5 ? 'PASS' : 'FAIL', '< 5%'),
		metric('oos', 'Out of Sequence', 'Finish-to-start predecessor is not complete while the successor has progress.', oosCount, discrete.length, oosCount === 0 ? 'PASS' : 'FAIL', '0 allowed'),
		metric('loe', 'LOE Driving Logic', 'Level-of-effort activities on the driving path.', loeDriving, loeTasks.length, loeDriving === 0 ? 'PASS' : 'FAIL', '0 allowed'),
		metric('stalled', 'Active at 0%', 'Activities in progress with percent complete still at 0.', stalled.length, incompleteTasks.length, stalled.length === 0 ? 'PASS' : 'WARNING', '0 preferred')
	];
	if (costKnown) {
		metrics.push(metric('uncosted', 'Uncosted Work', 'Work activities with no target cost.', uncostedCount, work.length, uncostedCount === 0 ? 'PASS' : 'WARNING', '0 preferred'));
	}
	return metrics;
}
